/**
 * Subdomain Suggestions Modal Component
 * Displays AI-powered suggestions for additional subdomains
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Plus,
  Info,
} from 'lucide-react';

import { getSubdomainSuggestions, addSubdomain } from '../../lib/api/domainPacksApi';

interface SubdomainSuggestionsModalProps {
  domainPackId: string;
  domainPackName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface Suggestion {
  name: string;
  description: string;
  scope: string;
  rationale: string;
}

export const SubdomainSuggestionsModal: React.FC<SubdomainSuggestionsModalProps> = ({
  domainPackId,
  domainPackName,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());

  // Fetch suggestions
  const { data, isLoading, error } = useQuery({
    queryKey: ['subdomain-suggestions', domainPackId],
    queryFn: () => getSubdomainSuggestions(domainPackId, 5),
    enabled: isOpen,
  });

  // Mutation for adding selected subdomains
  const addMutation = useMutation({
    mutationFn: async (suggestion: Suggestion) => {
      return await addSubdomain(
        domainPackId,
        suggestion.name,
        suggestion.description,
        suggestion.scope,
        true // Run research by default
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-pack', domainPackId] });
      queryClient.invalidateQueries({ queryKey: ['domain-packs'] });
    },
  });

  const handleToggleSelection = (index: number) => {
    const newSelected = new Set(selectedSuggestions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSuggestions(newSelected);
  };

  const handleAddSelected = async () => {
    if (!data || selectedSuggestions.size === 0) return;

    const suggestions = data.suggestions;
    const selected = Array.from(selectedSuggestions).map(i => suggestions[i]);

    // Add all selected suggestions
    for (const suggestion of selected) {
      try {
        await addMutation.mutateAsync(suggestion);
      } catch (e) {
        console.error('Failed to add suggestion:', e);
      }
    }

    // Close modal and navigate to domain pack to see results
    handleClose();
  };

  const handleClose = () => {
    setSelectedSuggestions(new Set());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-primary-600" />
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">
                AI Subdomain Suggestions
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                For: <span className="font-semibold">{domainPackName}</span>
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            disabled={addMutation.isPending}
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              <span className="ml-3 text-neutral-700">Generating suggestions...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Failed to generate suggestions</p>
                <p className="text-sm text-red-700 mt-1">
                  {(error as any)?.response?.data?.detail || 'An error occurred'}
                </p>
              </div>
            </div>
          )}

          {data && (
            <div className="space-y-4">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-primary-900">
                  <p className="font-semibold">AI-Powered Analysis</p>
                  <p className="mt-1">
                    These suggestions are based on analyzing your existing subdomains and identifying gaps in coverage.
                    Select the ones you want to add (research will be triggered automatically).
                  </p>
                </div>
              </div>

              {data.suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedSuggestions.has(index)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                  onClick={() => handleToggleSelection(index)}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {selectedSuggestions.has(index) ? (
                        <CheckCircle2 className="w-5 h-5 text-primary-600" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-neutral-300 rounded" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-neutral-900">{suggestion.name}</h3>
                      <p className="text-sm text-neutral-700 mt-1">{suggestion.description}</p>

                      {suggestion.scope && (
                        <div className="mt-2 text-sm">
                          <span className="font-semibold text-neutral-900">Scope: </span>
                          <span className="text-neutral-700">{suggestion.scope}</span>
                        </div>
                      )}

                      <div className="mt-2 bg-white rounded p-2 border border-neutral-200">
                        <span className="font-semibold text-neutral-900 text-sm">Why this matters: </span>
                        <span className="text-neutral-700 text-sm">{suggestion.rationale}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-neutral-200">
          <div className="text-sm text-neutral-600">
            {selectedSuggestions.size > 0 && (
              <span className="font-semibold text-primary-600">
                {selectedSuggestions.size} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              disabled={addMutation.isPending}
            >
              Cancel
            </button>
            <button
              onClick={handleAddSelected}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={selectedSuggestions.size === 0 || addMutation.isPending}
            >
              {addMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Selected ({selectedSuggestions.size})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
